import { Button, Typography } from '@mui/material';
import {
  AthleteActualWorkoutDomain,
  getWorkoutName,
  PublishedWorkoutStatusDomain,
  WorkoutStatus,
} from '@strength/domain';
import {
  getWorkoutExerciseIds,
  mergeAthleteWorkoutFromLayers,
  mergeAthleteWorkoutLayers,
} from '@strength/domain-workout';
import { getRouteApi, useRouter } from '@tanstack/react-router';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from '@teamworksdev/icons';
import { inArray } from '@teamworksdev/loco-sync-client';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '../../../loco-sync';
import { trpc } from '../../../trpc/react';
import { WorkoutStatusBadge } from '../../components/badge/workout-status-badge';
import { EmptyPlaceholder } from '../../components/illustrations/EmptyPlaceholder';
import {
  MultiSelect,
  SelectableOption,
} from '../../components/input/multi-select';
import { StringDatePicker } from '../../components/input/string-date-picker';
import { Page } from '../../components/layout/page';
import { PageHeader } from '../../components/layout/page-header';
import { TeamSelect } from '../../components/layout/TeamSelect';
import { useViewer } from '../../context/viewer';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useExerciseBenchmarks } from '../../hooks/useExerciseBenchmarks';
import { useKpiBenchmarks } from '../../hooks/useKpiBenchmarks';
import { useToast } from '../../hooks/useToast';
import { ActualsReadOnlyBanner } from './components/ActualsReadOnlyBanner';
import { WorkoutLayerCommand } from '../block/mutation-fns/types';
import { trackEvent } from './../../../reporting/reporting';
import {
  ActualWorkoutLayerMutationFns,
  makeActualWorkoutLayerMutationFns,
  runAthleteActualsMutations,
} from './actuals-mutations';
import { AmsSyncHeader } from './ams-sync-header';
import { CompleteWorkoutHeader } from './complete-workout-header';
import { AthleteStatusAccordion } from './components/AthleteStatusAccordion';
import { ExerciseActuals } from './exercise-actuals';
import { ActualsSetFilter, SetSelect } from './set-select';
import { MetricOption, WorkoutMutationContext } from './types';
import {
  excludePlaceholderLayers,
  getAthleteWorkoutsForLayers,
  getOptionsFromWorkouts,
  getUniqueExerciseOptions,
} from './utils';
import { WorkoutOption, WorkoutSelect } from './workout-select';

const routeApi = getRouteApi('/orgAuth/orgWithNav/$teamId/actuals/$date');

export const ActualsPage = () => {
  const {
    team: { selectedTeam: teamId },
    hasPermissions,
  } = useViewer();
  const canEditActuals = hasPermissions('actuals', 'edit');
  const actualsReadOnly = !canEditActuals;

  // validate the date param, if it's not a valid date, use the current date
  const { date: routeDate } = routeApi.useParams();
  const date =
    routeDate && DateTime.fromISO(routeDate).isValid
      ? routeDate
      : DateTime.now().toISODate();

  const { navigate } = useRouter();
  const { toast } = useToast();
  const { t } = useTranslation();

  // The bootstrap collects workouts for the selected day only
  const syncGroups = useMemo(
    () => [
      {
        type: 'TeamCoach_BulkActuals' as const,
        teamId,
        date,
        timezone: DateTime.local().zoneName,
      },
    ],
    [teamId, date],
  );

  const { isLoading } = useBootstrap(syncGroups);

  const [setFilter, setSetFilter] = useState<ActualsSetFilter>('all-sets');
  const [selectedMetrics, setSelectedMetrics] = useState<MetricOption[]>([]);
  const [selectedAthleteIds, setSelectedAthleteIds] = useState<string[]>([]);
  const [selectedExerciseOptions, setSelectedExerciseOptions] = useState<
    SelectableOption[]
  >([]);

  const onResetSelections = () => {
    setSelectedWorkout(null);
    setSelectedAthleteIds([]);
    setSelectedExerciseOptions([]);
    setSelectedMetrics([]);
  };

  const orgDetailResult = trpc.orgSettings.orgDetail.useQuery(undefined, {
    staleTime: 10_000,
  });
  const orgData = orgDetailResult.data;

  const teamAthleteResult = trpc.roster.teamAthletes.useQuery(
    {},
    {
      staleTime: 10_000,
    },
  );
  const teamAthletes = teamAthleteResult.data || [];

  const tracksQuery = trpc.plan.tracksForTeam.useQuery(undefined, {
    staleTime: 10_000,
  });
  const { data: blocks } = useQuery('Block', {
    trackId: inArray(tracksQuery.data?.map((a) => a.id) ?? []),
  });

  // get all athlete layers with a matching base layer for the day
  const { data: baseLayers } = useQuery('BlockPublishedWorkoutLayer', {
    assignedDate: date,
    isCurrent: true,
    blockId: inArray(blocks.map((b) => b.id)),
  });
  const { data: athleteMatchedLayersRaw } = useQuery(
    'AthletePublishedWorkoutLayer',
    {
      isCurrent: true,
      profileId: inArray(teamAthletes.map((a) => a.id)),
      workoutId: inArray(baseLayers.map((b) => b.workoutId)),
      blockId: inArray(blocks.map((b) => b.id)),
      status: inArray(['planned', 'completed']),
    },
    {
      block: {},
      actual: {},
      modifier: {},
    },
  );

  const { data: blockLayers } = useQuery('BlockPublishedWorkoutLayer', {
    isCurrent: true,
    workoutId: inArray(athleteMatchedLayersRaw.map((a) => a.workoutId)),
  });

  const athleteMatchedLayers = useMemo(() => {
    return athleteMatchedLayersRaw.map((layer) => {
      const blockLayer = blockLayers.find(
        (block) => block.workoutId === layer.workoutId,
      );

      return {
        ...layer,
        blockLayer,
      };
    });
  }, [athleteMatchedLayersRaw, blockLayers]);

  // prescribed layers assigned to this specific date
  const { data: athleteDateLayers } = useQuery(
    'AthletePublishedWorkoutLayer',
    {
      assignedDate: date,
      isCurrent: true,
      profileId: inArray(teamAthletes.map((a) => a.id)),
      blockId: inArray(blocks.map((b) => b.id)),
    },
    {
      block: {},
      blockLayer: {},
      actual: {},
      modifier: {},
    },
  );

  // completed flex (undated) layers — client-side filtered to those completed on this UTC day
  const { data: athleteFlexLayersRaw } = useQuery(
    'AthletePublishedWorkoutLayer',
    {
      assignedDate: null,
      status: 'completed',
      isCurrent: true,
      profileId: inArray(teamAthletes.map((a) => a.id)),
      blockId: inArray(blocks.map((b) => b.id)),
    },
    {
      block: {},
      blockLayer: {},
      actual: {},
      modifier: {},
    },
  );

  const athleteLayers = useMemo(() => {
    // Use local timezone boundaries so "May 1" means local midnight-to-midnight,
    // not UTC. statusUpdatedAt carries its own timezone (ISO with Z) so Luxon
    // compares the two instants correctly regardless of their zones.
    const dayStart = DateTime.fromISO(date).startOf('day');
    const dayEnd = dayStart.plus({ days: 1 });

    const result = [...athleteMatchedLayers];

    for (const layer of athleteDateLayers) {
      if (layer.blockLayer) continue;
      if (!result.find((l) => l.workoutId === layer.workoutId)) {
        result.push(layer);
      }
    }

    for (const layer of athleteFlexLayersRaw) {
      if (!layer.statusUpdatedAt) continue;
      const ts = DateTime.fromISO(layer.statusUpdatedAt);
      if (ts >= dayStart && ts < dayEnd) {
        if (
          !result.find(
            (l) =>
              l.workoutId === layer.workoutId &&
              l.profileId === layer.profileId,
          )
        ) {
          result.push(layer);
        }
      }
    }

    return excludePlaceholderLayers(result);
  }, [athleteMatchedLayers, athleteDateLayers, athleteFlexLayersRaw, date]);

  const [selectedWorkout, setSelectedWorkout] = useState<WorkoutOption | null>(
    null,
  );

  const workoutOptions: WorkoutOption[] = useMemo(() => {
    const options = Array.from(
      new Set(athleteLayers.map((w) => w.workoutId)),
    ).flatMap((workoutId) => {
      if (
        athleteLayers.every(
          (w) => w.workoutId === workoutId && w.status === 'canceled',
        )
      ) {
        return [];
      }
      const workout = athleteLayers.find((w) => w.workoutId === workoutId);
      if (!workout || !workout.block) {
        return [];
      }
      const block = workout.block;
      const track = tracksQuery.data?.find(
        (track) => track.id === block?.trackId,
      );
      const workoutNameParams = {
        cycleSortOrder: workout.cycleSortOrder,
        sessionSortOrder: workout.sessionSortOrder,
        // if the workout isn't an individual layer, use the block layer name, else use the workout name
        name: workout.blockLayer?.name ?? workout.name,
      };
      return {
        id: workoutId,
        name: track
          ? `${track.name}: ${getWorkoutName(workoutNameParams, block)}`
          : getWorkoutName(workoutNameParams, block),
      };
    });

    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [athleteLayers, tracksQuery.data]);

  const selectedLayers = useMemo(
    () =>
      athleteLayers.filter(
        (l) => l.workoutId === selectedWorkout?.id && l.status !== 'canceled',
      ),
    [athleteLayers, selectedWorkout],
  );
  const athleteIdOptions = useMemo(
    () => new Set(selectedLayers.map((layer) => layer.profileId)),
    [selectedLayers],
  );
  const athleteOptions = useMemo(
    () => teamAthletes.filter((athlete) => athleteIdOptions.has(athlete.id)),
    [teamAthletes, athleteIdOptions],
  );

  const selectedAthleteIdSet = useMemo(
    () => new Set(selectedAthleteIds),
    [selectedAthleteIds],
  );

  const selectedAthleteLayers = useMemo(
    () =>
      selectedLayers.filter((layer) =>
        selectedAthleteIdSet.has(layer.profileId),
      ),
    [selectedLayers, selectedAthleteIdSet],
  );

  const selectedTrackId = useMemo(() => {
    const layer = selectedLayers[0];
    return layer?.block?.trackId ?? null;
  }, [selectedLayers]);

  const libraryExerciseIds = useMemo(() => {
    return selectedAthleteLayers.flatMap((layer) => {
      const merged = mergeAthleteWorkoutLayers(layer.blockLayer, layer);
      if (!merged) return [];
      return getWorkoutExerciseIds(merged);
    });
  }, [selectedAthleteLayers]);

  const { kpiBenchmarksByProfile } = useKpiBenchmarks({
    trackId: selectedTrackId ?? undefined,
  });

  const { exerciseBenchmarks, conversions } = useExerciseBenchmarks({
    libraryExerciseIds,
    trackId: selectedTrackId ?? '',
  });

  const mergedAthleteWorkouts = useMemo(
    () =>
      selectedAthleteLayers.length > 0
        ? getAthleteWorkoutsForLayers(
            selectedAthleteLayers,
            exerciseBenchmarks,
            conversions,
          )
        : [],
    [selectedAthleteLayers, exerciseBenchmarks, conversions],
  );

  const { exerciseOptions, metricOptions } = useMemo(
    () => getOptionsFromWorkouts(mergedAthleteWorkouts, t),
    [mergedAthleteWorkouts, t],
  );

  const handleExerciseSelection = (options: SelectableOption[]) => {
    const wasAllSelected = selectedExerciseOptions.length === 0;
    const isAllSelected = options.length === 0;

    if (!wasAllSelected && isAllSelected) {
      setSelectedExerciseOptions([]);
    } else {
      setSelectedExerciseOptions(options);
    }
  };

  const mutation = useMutation();
  const mutationFns = useMemo(() => {
    const actualLayers = selectedAthleteLayers.flatMap(
      (layer) => layer.actual ?? [],
    );
    const bulkMutate = (
      commands: readonly AthleteActualWorkoutDomain.Command[],
    ) => {
      for (const command of commands) {
        mutation.mutate(command);
      }
    };

    const runCommands = (
      context: WorkoutMutationContext,
      commands: WorkoutLayerCommand[],
    ) => {
      // Find the athlete layer for this specific context
      const athleteLayer = selectedAthleteLayers.find(
        (layer) =>
          layer.profileId === context.profileId &&
          commands.some((cmd) => cmd.identity.workoutId === layer.workoutId),
      );

      const additionalLayers = athleteLayer
        ? {
            athletePublishedWorkoutLayer: athleteLayer.layer,
            blockPublishedWorkoutLayer:
              athleteLayer.blockLayer?.layer || undefined,
            athleteModifiedPublishedWorkoutLayer:
              athleteLayer.modifier?.layer || undefined,
          }
        : undefined;

      runAthleteActualsMutations(
        context,
        commands,
        bulkMutate,
        additionalLayers,
      );
    };
    const fns = makeActualWorkoutLayerMutationFns(
      actualLayers,
      (context, command) => runCommands(context, [command]),
    );
    return {
      ...fns,
      // Practitioner bulk entry: stamp every set-metric edit 'Manual Override'
      // (rank 0 in ACTUALS_SOURCE_RANKING) so it keeps outranking Catapult /
      // Player Entered feed values that sync in after the fact. The tablet
      // route and the mobile app record athlete-entered actuals and stay
      // un-stamped on purpose.
      onEditSetMetric: ((context, workoutId, workoutSetId, data) =>
        fns.onEditSetMetric(context, workoutId, workoutSetId, {
          ...data,
          dataSource: 'Manual Override',
        })) satisfies ActualWorkoutLayerMutationFns['onEditSetMetric'],
    };
  }, [selectedAthleteLayers, mutation]);

  const athletesByStatus = useMemo(() => {
    const statusByProfileId = new Map<string, string>();
    for (const layer of selectedLayers) {
      statusByProfileId.set(layer.profileId, layer.status);
    }
    const planned: typeof athleteOptions = [];
    const completed: typeof athleteOptions = [];
    for (const athlete of athleteOptions) {
      const status = statusByProfileId.get(athlete.id);
      if (status === 'planned') planned.push(athlete);
      else if (status === 'completed') completed.push(athlete);
    }
    return { planned, completed };
  }, [selectedLayers, athleteOptions]);

  const noWorkoutsForDay = workoutOptions.length === 0;
  const noAthletesSelected = selectedAthleteIds.length === 0;

  const uniqueExerciseOptions = useMemo(
    () => getUniqueExerciseOptions(exerciseOptions),
    [exerciseOptions],
  );

  const onToggleAthlete = useCallback((athleteId: string, checked: boolean) => {
    setSelectedAthleteIds((prev) =>
      checked ? [...prev, athleteId] : prev.filter((id) => id !== athleteId),
    );
  }, []);

  const onSelectAllForStatus = useCallback((athleteIds: string[]) => {
    setSelectedAthleteIds((prev) => {
      const existing = new Set(prev);
      const toAdd = athleteIds.filter((id) => !existing.has(id));
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const onDeselectAllForStatus = useCallback((athleteIds: string[]) => {
    setSelectedAthleteIds((prev) => {
      const removeSet = new Set(athleteIds);
      return prev.filter((id) => !removeSet.has(id));
    });
  }, []);

  const disableActionScreen = selectedWorkout === null || noAthletesSelected;

  const onUpdateWorkoutStatuses = async (
    updates: {
      profileId: string;
      status: WorkoutStatus;
    }[],
  ) => {
    if (selectedWorkout && selectedLayers[0]) {
      const blockId = selectedLayers[0].blockId;
      // Transform updates to include workout layers for each athlete
      const updatesWithLayers = updates.map((update) => {
        const athletePublishedLayer = athleteLayers.find(
          (layer) =>
            layer.workoutId === selectedWorkout?.id &&
            layer.profileId === update.profileId,
        );
        const athleteModifiedLayer = athletePublishedLayer?.modifier?.layer;
        const athleteActualLayer = athletePublishedLayer?.actual?.layer;
        const blockPublishedLayer = athletePublishedLayer?.blockLayer?.layer;

        const workout = mergeAthleteWorkoutFromLayers(
          selectedWorkout?.id,
          blockPublishedLayer,
          athletePublishedLayer?.layer,
          athleteModifiedLayer,
          athleteActualLayer,
        );

        if (!workout) {
          throw new Error('Failed to merge workout layers');
        }

        return {
          profileId: update.profileId,
          status: update.status,
          workout,
        };
      });

      const command: PublishedWorkoutStatusDomain.Command = {
        module: PublishedWorkoutStatusDomain.Module,
        type: 'UpdateAthleteWorkoutStatus',
        input: {
          blockId,
          workoutId: selectedWorkout?.id,
          timezone: DateTime.local().zoneName,
          updates: updatesWithLayers,
        },
      };
      mutation.mutate(command);

      trackEvent('Strength | Bulk Actuals | Update Workout Statuses', {
        workoutId: selectedWorkout?.id,
      });
      toast({
        title: t('success'),
        description: t('changesSaved'),
        severity: 'success',
      });
    }
  };

  const handlePreviousDay = () => {
    const previousDay = DateTime.fromISO(date).minus({ days: 1 }).toISODate();
    if (!previousDay) {
      return;
    }
    navigate({
      to: '/$teamId/actuals/$date',
      params: { teamId, date: previousDay },
    });
  };

  const handleNextDay = () => {
    const nextDay = DateTime.fromISO(date).plus({ days: 1 }).toISODate();
    if (!nextDay) {
      return;
    }
    navigate({
      to: '/$teamId/actuals/$date',
      params: { teamId, date: nextDay },
    });
  };
  return (
    <Page>
      <PageHeader
        title={t('actuals')}
        rightSlot={
          <TeamSelect
            sx={{ mt: 0 }}
            teamId={teamId}
            onSelect={(newTeamId) => {
              onResetSelections();
              navigate({
                to: '/$teamId/actuals/$date',
                params: { teamId: newTeamId, date },
              });
            }}
          />
        }
      />
      <div className="flex flex-row flex-1 min-h-0 gap-10 ml-6 mr-8 overflow-hidden">
        <div className="flex flex-col w-1/4 overflow-hidden border-r border-r-1 pr-3 pt-4 gap-2 min-h-0 min-w-[256px]">
          <div className="pr-3 flex flex-col gap-2">
            <div className="flex flex-row  gap-2 justify-between items-center">
              <StringDatePicker
                value={date}
                onChange={(newValue) => {
                  if (newValue) {
                    onResetSelections();
                    navigate({
                      to: '/$teamId/actuals/$date',
                      params: {
                        teamId,
                        date: newValue || DateTime.now().toISODate(),
                      },
                    });
                  }
                }}
              />
              <div className="flex flex-row mt-3">
                <Button
                  onClick={handlePreviousDay}
                  variant="outlined"
                  size="small"
                  aria-label={t('previousDay')}
                >
                  <MdKeyboardArrowLeft size={24} />
                </Button>
                <Button
                  onClick={handleNextDay}
                  sx={{ ml: 1 }}
                  variant="outlined"
                  size="small"
                  aria-label={t('nextDay')}
                >
                  <MdKeyboardArrowRight size={24} />
                </Button>
              </div>
            </div>
            <WorkoutSelect
              loading={isLoading}
              workoutOptions={workoutOptions}
              selectedWorkout={selectedWorkout}
              onSelectWorkout={setSelectedWorkout}
            />
          </div>
          <div className="flex flex-col flex-1 min-h-0 overflow-y-auto mt-2 pr-3 [scrollbar-gutter:stable]">
            <AthleteStatusAccordion
              status="planned"
              athletes={athletesByStatus.planned}
              selectedAthleteIdSet={selectedAthleteIdSet}
              onToggleAthlete={onToggleAthlete}
              onSelectAll={onSelectAllForStatus}
              onDeselectAll={onDeselectAllForStatus}
            />
            <AthleteStatusAccordion
              status="completed"
              athletes={athletesByStatus.completed}
              selectedAthleteIdSet={selectedAthleteIdSet}
              onToggleAthlete={onToggleAthlete}
              onSelectAll={onSelectAllForStatus}
              onDeselectAll={onDeselectAllForStatus}
            />
            {noWorkoutsForDay && athleteOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('noAssignedWorkouts')}
              </Typography>
            )}
          </div>
        </div>
        <main
          id="actuals"
          className="flex min-h-0 min-w-[280px] flex-1 flex-col gap-4 w-full pt-4 pb-2 pr-6"
        >
          <ActualsReadOnlyBanner readOnly={actualsReadOnly} />
          <div
            id="workout-header"
            className="flex shrink-0 flex-col gap-4 border-b border-gray-200 pb-2"
          >
            {selectedWorkout && (
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Typography variant="h3" className="truncate">
                    {selectedWorkout?.name}
                  </Typography>
                  {/** Display badge if all workouts completed */}
                  {selectedLayers.filter((s) => s.status === 'completed')
                    .length === selectedLayers.length && (
                    <WorkoutStatusBadge
                      status={{
                        type: 'completed',
                        completed: 1,
                        toComplete: 1,
                      }}
                    />
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {orgData?.hasAmsIntegration && (
                    <AmsSyncHeader
                      disabled={noWorkoutsForDay}
                      workouts={selectedLayers.map((sl) => ({
                        profileId: sl.profileId,
                        blockId: sl.blockId,
                        workoutId: sl.workoutId,
                      }))}
                    />
                  )}
                  {selectedWorkout && (
                    <CompleteWorkoutHeader
                      workoutId={selectedWorkout?.id}
                      workoutName={selectedWorkout?.name || ''}
                      athletes={athleteOptions}
                      athleteWorkouts={selectedLayers}
                      onSaveWorkoutStatuses={onUpdateWorkoutStatuses}
                    />
                  )}
                </div>
              </div>
            )}
            <div className="-mt-3 flex max-w-full items-center gap-1 pt-0 w-[600px]">
              <div className="w-48">
                <MultiSelect
                  disabled={disableActionScreen}
                  selectedOptions={selectedExerciseOptions}
                  options={uniqueExerciseOptions}
                  placeholder={t('allExercises')}
                  onChange={handleExerciseSelection}
                  optionToName={(e) => e.name}
                  ariaLabel={t('selectExercise')}
                />
              </div>
              <div className="w-48">
                <MultiSelect
                  options={metricOptions}
                  selectedOptions={selectedMetrics}
                  onChange={setSelectedMetrics}
                  optionToName={(m) => m.name}
                  placeholder={t('allMetrics')}
                  ariaLabel={t('selectMetric')}
                />
              </div>
              <SetSelect
                showLabel={false}
                filter={setFilter}
                setFilter={setSetFilter}
                disabled={disableActionScreen}
              />
            </div>
          </div>
          <div className="min-h-0 min-w-0 flex-1 w-full overflow-y-auto">
            {noWorkoutsForDay ? (
              <NoWorkoutsWarning />
            ) : noAthletesSelected ? (
              <NoSelectionWarning />
            ) : (
              <ExerciseActuals
                exerciseBenchmarks={exerciseBenchmarks}
                conversions={conversions}
                athleteWorkouts={mergedAthleteWorkouts}
                exerciseSelection={
                  selectedExerciseOptions.length === 0
                    ? exerciseOptions
                    : exerciseOptions.filter((option) =>
                        selectedExerciseOptions.some(
                          (selected) =>
                            selected.id === option.libraryExerciseId,
                        ),
                      )
                }
                metricSelection={
                  selectedMetrics.length === 0 ? metricOptions : selectedMetrics
                }
                athleteSelection={teamAthletes.filter((athlete) =>
                  selectedAthleteIdSet.has(athlete.id),
                )}
                setFilter={setFilter}
                mutationFns={mutationFns}
                trainingMaxRatio={
                  // Actuals only shows workouts from a single block at a time,
                  // so we can be sure that any of the workouts displaying have the same block id
                  blocks.find(
                    (b) => b.id === mergedAthleteWorkouts[0]?.context.blockId,
                  )?.trainingMaxRatio ?? 1
                }
                readOnly={actualsReadOnly}
                kpiBenchmarksByProfile={kpiBenchmarksByProfile}
              />
            )}
          </div>
        </main>
      </div>
    </Page>
  );
};

const NoSelectionWarning = () => {
  const { t } = useTranslation();
  return (
    <EmptyPlaceholder
      title={t('addAthletesToViewActuals')}
      message={t('selectAthletesFromList')}
    />
  );
};

const NoWorkoutsWarning = () => {
  const { t } = useTranslation();
  return (
    <EmptyPlaceholder
      title={t('noWorkoutsAssignedOnThisDay')}
      message={t('selectDifferentDayOrTeam')}
    />
  );
};
